import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Save, Check } from "lucide-react";
import api, { formatErr } from "../../lib/apiClient";

/**
 * Shared HCP-style settings-tab shell used by Item 9's four tabs:
 * Company Info · Notifications · Estimates · Jobs.
 * - Loads/saves the global settings document via /api/settings.
 * - Renders a back-link to the Settings hub, page title, optional sub-tab strip,
 *   and a Save button footer with success toast.
 * Children get: `settings`, `patch(partial)`, `saving`.
 */
export function SettingsShell({ title, subtitle, testid, subTabs, activeSubTab, onSubTabChange, children }) {
  return (
    <div data-testid={testid} className="max-w-5xl">
      <Link to="/settings" data-testid={`${testid}-back`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0F4C81] mb-3">
        <ArrowLeft size={14} /> Back to Settings
      </Link>
      <div className="border-b border-slate-200 pb-4 mb-6">
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>

      {subTabs && subTabs.length > 0 && (
        <div data-testid={`${testid}-subtabs`} className="flex flex-wrap gap-1 border-b border-slate-200 mb-6">
          {subTabs.map((t) => (
            <button
              key={t.key}
              type="button"
              data-testid={`${testid}-subtab-${t.key}`}
              onClick={() => onSubTabChange(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeSubTab === t.key
                  ? "border-[#0F4C81] text-[#0F4C81]"
                  : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {children}
    </div>
  );
}

/**
 * Hook: load settings on mount, expose a patch fn (partial update state), and a save fn.
 * Persists using PUT /api/settings.
 */
export function useSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).finally(() => setLoading(false));
  }, []);

  const patch = (partial) => setSettings((prev) => ({ ...(prev || {}), ...partial }));

  const save = async () => {
    if (!settings) return;
    setSaving(true); setErr(""); setSaved(false);
    try {
      // hst_rate must be numeric for the pydantic model
      const payload = { ...settings, hst_rate: Number(settings.hst_rate ?? 13) };
      await api.put("/settings", payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  return { settings, patch, save, loading, saving, saved, err };
}

/** Save bar rendered at the bottom of every settings tab. */
export function SaveBar({ onSave, saving, saved, err, testid }) {
  return (
    <div data-testid={`${testid}-savebar`} className="mt-8 pt-4 border-t border-slate-200 flex items-center gap-3">
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        data-testid={`${testid}-save`}
        className="px-5 py-2 bg-[#0F4C81] hover:bg-[#0A365C] disabled:opacity-60 text-white text-sm rounded-md font-medium flex items-center gap-2"
      >
        <Save size={14} /> {saving ? "Saving…" : "Save changes"}
      </button>
      {saved && (
        <span data-testid={`${testid}-saved`} className="text-sm text-emerald-700 flex items-center gap-1.5">
          <Check size={14} /> Saved
        </span>
      )}
      {err && <span className="text-sm text-red-600">{err}</span>}
    </div>
  );
}

/** Toggle row — used heavily across all four tabs. */
export function ToggleRow({ label, description, checked, onChange, disabled, testid, comingSoon }) {
  return (
    <div className="flex items-start justify-between py-3 border-b border-slate-100 gap-6">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-900">{label}</span>
          {comingSoon && <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Coming soon</span>}
        </div>
        {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        data-testid={testid}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${
          disabled ? "bg-slate-200 cursor-not-allowed" : checked ? "bg-[#0F4C81]" : "bg-slate-300 hover:bg-slate-400"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
    </div>
  );
}

/** Checkbox row — used for Estimates → Customer view field toggles. */
export function CheckboxRow({ label, checked, onChange, testid, description }) {
  return (
    <label className="flex items-start gap-2 py-1.5 cursor-pointer">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#0F4C81] focus:ring-[#0F4C81]"
      />
      <span className="flex-1">
        <span className="text-sm text-slate-900">{label}</span>
        {description && <span className="block text-xs text-slate-500">{description}</span>}
      </span>
    </label>
  );
}
